import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Menu, X, Star, Minus, Plus, Check, Instagram, Facebook, Leaf, Brain, Package, Mail, TrendingUp, Users, ShoppingBag, Eye, MessageSquare, DollarSign, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brand Assets
const ASSETS = {
  mainLogo: "https://customer-assets.emergentagent.com/job_d9683fa1-9f63-4d0a-9356-368081ffb196/artifacts/99zobgrb_gawdbrand.PNG",
  gawdBodyLogo: "https://customer-assets.emergentagent.com/job_d9683fa1-9f63-4d0a-9356-368081ffb196/artifacts/w7rwy6ap_DCD46AF1-3C0C-42BF-B642-4728EE1DE485.PNG",
  gawdMindLogo: "https://customer-assets.emergentagent.com/job_d9683fa1-9f63-4d0a-9356-368081ffb196/artifacts/23vk3xbk_38C1EB84-0D25-4F56-9BE2-42515AC9BEF4.PNG",
  productPhoto: "https://customer-assets.emergentagent.com/job_d9683fa1-9f63-4d0a-9356-368081ffb196/artifacts/3wgfiis8_IMG_5140.JPG",
  reviewsCollage: "https://customer-assets.emergentagent.com/job_d9683fa1-9f63-4d0a-9356-368081ffb196/artifacts/reofyc7g_gawdblends_reviews_collage.png"
};

// Reviews Data
const REVIEWS = [
  { text: "I just received it and thank you for real. I haven't gotten sick in two years.", author: "Verified Community Customer" },
  { text: "I've been taking this daily for weeks now and I'm really starting to see and feel the benefits. My energy is easier to maintain, brain fog reduced, and productivity is up. This product tastes great and works gently.", author: "Verified Amazon Review" },
  { text: "I LOVE this blend. The quality of my skin, hair, digestion, and energy has improved significantly since I started taking it consistently. It's pretty much replaced my multivitamin.", author: "Verified Amazon Review" },
  { text: "You wouldn't think something with all of that would taste this good, but it does.", author: "Community Review" },
  { text: "I love this blend because it's so easy to use. Pills are hard for me to remember, so having something versatile makes staying consistent easy.", author: "Verified Amazon Review" },
  { text: "Ever since my best friend gave me this, I've sworn by it. Great job and an awesome product.", author: "Community Review" },
  { text: "I feel less anxious right away. I love that it's in Gawd Mind and I take mine every day.", author: "Community Review" },
  { text: "Best stuff ever.", author: "Community Review" }
];

// FAQ Data
const FAQS = [
  { question: "How long does one container last?", answer: "One container typically lasts 30 days when taken as directed - one scoop daily. For the bundle, you'll have both products lasting approximately one month each." },
  { question: "How do I take it?", answer: "Simply mix one scoop with water, juice, or your favorite smoothie. You can take it in the morning with breakfast or any time that fits your routine. The blends mix easily and taste great!" },
  { question: "Do you ship nationwide?", answer: "Yes! We ship to all 50 states within the US. Flat rate shipping is $6.00 per order — and the Gawd Bundle ships free." },
  { question: "Do you offer any discounts?", answer: "Yes! Subscribe to our newsletter for an instant 10% off code (WELCOME10). Enter the code at checkout. Valid on Gawd Body and Gawd Mind only." },
  { question: "When will my order ship?", answer: "Orders are processed within 1-2 business days. Once shipped, you'll receive a tracking number via email. Delivery typically takes 3-7 business days depending on your location." },
  { question: "Who should consult a physician first?", answer: "If you are pregnant, nursing, taking medication, or have a medical condition, please consult your healthcare provider before using our products. Our blends contain natural herbs that may interact with certain medications." }
];

// Ingredients Data
const BODY_INGREDIENTS = [
  "Spirulina", "Moringa", "Burdock Root", "Irish Moss", "Beet Root", "Atlantic Kelp", 
  "Bee Pollen", "Acai", "Noni Fruit", "Alfalfa Leaf", "Wheatgrass", "Chlorella", 
  "Dandelion", "Nettle Leaf", "Parsley Leaf", "Parsley Root", "Rosehips", "Spinach", 
  "Sassafras", "Maca", "Camu Camu", "Triphala", "Tomato", "Bladderwrack"
];

const MIND_INGREDIENTS = [
  "Rhodiola", "Ashwagandha", "Maca Root", "Red Maca", "Lion's Mane", "Gotu Kola"
];

const VITAMINS = ["Vitamins A, C, K, B", "9 Essential Amino Acids", "Several Minerals"];

// Header Component
const Header = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#shop", label: "Shop" },
    { href: "#ingredients", label: "Ingredients" },
    { href: "#reviews", label: "Reviews" },
    { href: "#story", label: "Our Story" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-sm" : "bg-white/60"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="/" data-testid="header-logo">
          <img src={ASSETS.mainLogo} alt="Gawd Blends" className="h-10 md:h-12" />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map(link => (
            <a key={link.href} href={link.href} className="nav-link text-sm">
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a href="#shop">
            <Button 
              data-testid="order-now-btn"
              className="hidden md:flex bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-6 py-2 btn-lift font-body font-semibold"
            >
              Order Now
            </Button>
          </a>
          
          {/* Mobile Menu Toggle */}
          <button 
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-white/98 backdrop-blur-lg z-[100] md:hidden flex flex-col items-center justify-center gap-6" 
          data-testid="mobile-menu"
        >
          <button 
            className="absolute top-4 right-6 p-2"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X size={28} />
          </button>
          {navLinks.map(link => (
            <a 
              key={link.href} 
              href={link.href} 
              className="text-2xl font-medium text-[#1a1a1a] hover:text-[#1E3A8A] transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a href="#shop" onClick={() => setMobileMenuOpen(false)}>
            <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-4 mt-4 text-lg">
              Order Now
            </Button>
          </a>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => (
  <section data-testid="hero-section" className="pt-28 pb-16 md:pt-36 md:pb-24 hero-bg">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="text-center md:text-left">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] leading-tight mb-6">
            Daily blends for body + mind made to keep you consistent.
          </h1>
          <p className="text-lg md:text-xl text-[#6b7280] mb-8 font-body leading-relaxed">
            We created Gawd Body and Gawd Mind to make wellness simple. One scoop. One routine.
          </p>
          <a href="#shop">
            <Button 
              data-testid="hero-cta-btn"
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-4 text-lg btn-lift font-body font-bold tracking-wide"
            >
              Shop Now
            </Button>
          </a>
        </div>
        <div className="flex justify-center">
          <div className="product-image-container w-full max-w-md">
            <img 
              src={ASSETS.productPhoto} 
              alt="Gawd Blends Products"
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Product Card Component
const ProductCard = ({ product, onCheckout, loading }) => {
  const [quantity, setQuantity] = useState(1);
  const isBundle = product.id === "gawd_bundle";

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, Math.min(10, prev + delta)));
  };

  const getProductIcon = () => {
    if (product.id === "gawd_body") return <Leaf className="w-5 h-5" />;
    if (product.id === "gawd_mind") return <Brain className="w-5 h-5" />;
    return <Package className="w-5 h-5" />;
  };

  const shippingFee = isBundle ? 0 : 6;
  const finalTotal = product.price * quantity + shippingFee;

  return (
    <div 
      data-testid={`product-card-${product.id}`}
      className={`bg-white rounded-2xl p-6 md:p-8 border ${isBundle ? 'border-[#c5a059]' : 'border-gray-100'} 
        hover:border-[#c5a059]/50 transition-all duration-300 shadow-sm hover:shadow-md product-card relative`}
    >
      {isBundle && (
        <div className="absolute top-4 right-4">
          <span className="best-value-badge">Best Value</span>
        </div>
      )}
      
      <div className="flex justify-center mb-6">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-32 h-32 md:w-40 md:h-40 object-contain"
        />
      </div>
      
      <div className="flex items-center gap-2 mb-2 text-[#134E4A]">
        {getProductIcon()}
        <span className="text-sm font-semibold uppercase tracking-wider">{product.id.replace('_', ' ')}</span>
      </div>
      
      <h3 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-2">{product.name}</h3>
      <p className="text-[#6b7280] text-sm mb-4 leading-relaxed">{product.description}</p>
      
      <div className="flex items-center justify-between mb-6">
        <span className="text-3xl font-bold text-[#1E3A8A]">${product.price.toFixed(2)}</span>
        <span className="text-sm text-[#6b7280]">{isBundle ? "Free shipping" : "+ $6.00 shipping"}</span>
      </div>
      
      {/* Quantity Selector */}
      <div className="flex items-center justify-center gap-4 mb-6" data-testid={`quantity-selector-${product.id}`}>
        <button 
          className="quantity-btn"
          onClick={() => handleQuantityChange(-1)}
          disabled={quantity <= 1}
          data-testid={`qty-minus-${product.id}`}
        >
          <Minus size={16} />
        </button>
        <span className="text-xl font-semibold w-8 text-center">{quantity}</span>
        <button 
          className="quantity-btn"
          onClick={() => handleQuantityChange(1)}
          disabled={quantity >= 10}
          data-testid={`qty-plus-${product.id}`}
        >
          <Plus size={16} />
        </button>
      </div>
      
      <Button 
        data-testid={`buy-btn-${product.id}`}
        className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full py-4 btn-lift font-body font-bold"
        onClick={() => onCheckout(product.id, quantity)}
        disabled={loading}
      >
        {loading ? "Processing..." : `Add to Cart - $${finalTotal.toFixed(2)}`}
      </Button>
    </div>
  );
};

// Shop Section
const ShopSection = ({ products, onCheckout, loading }) => (
  <section id="shop" data-testid="shop-section" className="py-20 md:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <span className="text-sm uppercase tracking-widest font-bold text-[#c5a059] mb-4 block">Our Products</span>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1a1a]">Choose Your Blend</h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8 product-grid">
        {products.map(product => (
          <ProductCard 
            key={product.id} 
            product={product} 
            onCheckout={onCheckout}
            loading={loading}
          />
        ))}
      </div>
    </div>
  </section>
);

// Ingredients Section
const IngredientsSection = () => (
  <section id="ingredients" data-testid="ingredients-section" className="py-20 md:py-28 bg-[#fafafa]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <span className="text-sm uppercase tracking-widest font-bold text-[#c5a059] mb-4 block">What's Inside</span>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1a1a]">Premium Ingredients</h2>
      </div>
      
      <div className="grid md:grid-cols-2 gap-12">
        {/* Gawd Body Ingredients */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <img src={ASSETS.gawdBodyLogo} alt="Gawd Body" className="w-16 h-16 object-contain" />
            <div>
              <h3 className="font-heading text-2xl font-bold text-[#1a1a1a]">Gawd Body</h3>
              <p className="text-[#134E4A] font-medium">Full-Body Herbal Blend</p>
            </div>
          </div>
          <div className="flex flex-wrap">
            {BODY_INGREDIENTS.map(ing => (
              <span key={ing} className="ingredient-tag">{ing}</span>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-[#6b7280]">Plus: {VITAMINS.join(", ")}</p>
          </div>
        </div>
        
        {/* Gawd Mind Ingredients */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <img src={ASSETS.gawdMindLogo} alt="Gawd Mind" className="w-16 h-16 object-contain" />
            <div>
              <h3 className="font-heading text-2xl font-bold text-[#1a1a1a]">Gawd Mind</h3>
              <p className="text-[#134E4A] font-medium">Brain-Focused Herbal Blend</p>
            </div>
          </div>
          <div className="flex flex-wrap">
            {MIND_INGREDIENTS.map(ing => (
              <span key={ing} className="ingredient-tag">{ing}</span>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-[#6b7280]">Plus: {VITAMINS.join(", ")}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Reviews Section
const ReviewsSection = () => (
  <section id="reviews" data-testid="reviews-section" className="py-20 md:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <span className="text-sm uppercase tracking-widest font-bold text-[#c5a059] mb-4 block">Testimonials</span>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1a1a]">What Our Community Says</h2>
      </div>
      
      {/* Reviews Collage Image */}
      <div className="mb-12 flex justify-center">
        <img 
          src={ASSETS.reviewsCollage} 
          alt="Customer Reviews" 
          className="rounded-2xl shadow-lg max-w-full md:max-w-3xl"
        />
      </div>
      
      {/* Text Reviews */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {REVIEWS.map((review, idx) => (
          <div key={idx} className="review-card" data-testid={`review-card-${idx}`}>
            <div className="flex mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="star-gold fill-current" />
              ))}
            </div>
            <p className="text-[#374151] mb-4 text-sm leading-relaxed">"{review.text}"</p>
            <p className="text-xs text-[#9ca3af] font-medium">— {review.author}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Story Section
const StorySection = () => (
  <section id="story" data-testid="story-section" className="py-20 md:py-28 story-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-sm uppercase tracking-widest font-bold text-[#c5a059] mb-4 block">Our Journey</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">The Gawd Blends Story</h2>
          <div className="space-y-4 text-[#6b7280] leading-relaxed">
            <p>
              <strong className="text-[#1a1a1a]">It all started in 2017</strong> with a simple belief: wellness should be accessible, 
              effective, and actually enjoyable. We weren't satisfied with the complicated supplement routines and 
              hard-to-swallow pills that dominated the market.
            </p>
            <p>
              <strong className="text-[#1a1a1a]">By 2018-2019</strong>, we had perfected our blends and made them available to our 
              community. What started as a passion project quickly grew through word of mouth, authentic testimonials, 
              and a community that believed in what we were building.
            </p>
            <p>
              Today, Gawd Blends has helped thousands of people simplify their wellness routines. Our mission remains 
              the same: <strong className="text-[#1a1a1a]">one scoop, one routine, real results.</strong>
            </p>
          </div>
        </div>
        <div className="flex justify-center">
          <img 
            src={ASSETS.productPhoto} 
            alt="Gawd Blends Products" 
            className="rounded-2xl shadow-xl max-w-sm"
          />
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => (
  <section id="faq" data-testid="faq-section" className="py-20 md:py-28 bg-white">
    <div className="max-w-3xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <span className="text-sm uppercase tracking-widest font-bold text-[#c5a059] mb-4 block">Support</span>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1a1a]">Frequently Asked Questions</h2>
      </div>
      
      <Accordion type="single" collapsible className="w-full">
        {FAQS.map((faq, idx) => (
          <AccordionItem key={idx} value={`item-${idx}`} className="border-b border-gray-200">
            <AccordionTrigger 
              data-testid={`faq-trigger-${idx}`}
              className="text-left font-semibold text-lg text-[#1a1a1a] py-5 hover:no-underline"
            >
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-[#6b7280] pb-5 leading-relaxed">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Contact from ${formData.name}`);
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:gawdbodysolutions@gmail.com?subject=${subject}&body=${body}`;
    toast.success("Opening your email client...");
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-[#fafafa]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <span className="text-sm uppercase tracking-widest font-bold text-[#c5a059] mb-4 block">Get In Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-6">Contact Us</h2>
            <p className="text-[#6b7280] mb-8 leading-relaxed">
              Have questions about our products or your order? We'd love to hear from you. 
              Send us a message and we'll respond as soon as possible.
            </p>
            
            <div className="space-y-4">
              <p className="text-[#1a1a1a] font-medium">Follow us on social media:</p>
              <div className="flex gap-4">
                <a 
                  href="https://instagram.com/thegawdblend" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-icon flex items-center gap-2 text-[#6b7280] hover:text-[#1E3A8A]"
                  data-testid="social-instagram"
                >
                  <Instagram size={24} />
                  <span>Instagram</span>
                </a>
                <a 
                  href="https://tiktok.com/@itzomilola" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-icon flex items-center gap-2 text-[#6b7280] hover:text-[#1E3A8A]"
                  data-testid="social-tiktok"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
                  <span>TikTok</span>
                </a>
                <a 
                  href="https://facebook.com/TheGawdBodyBlend" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-icon flex items-center gap-2 text-[#6b7280] hover:text-[#1E3A8A]"
                  data-testid="social-facebook"
                >
                  <Facebook size={24} />
                  <span>Facebook</span>
                </a>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <form onSubmit={handleSubmit} className="contact-form space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-[#1a1a1a] mb-2">Name</label>
                <input 
                  type="text" 
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1a1a1a] mb-2">Email</label>
                <input 
                  type="email" 
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1a1a1a] mb-2">Message</label>
                <textarea 
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="How can we help you?"
                  data-testid="contact-message-input"
                />
              </div>
              <Button 
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full py-4 btn-lift font-body font-bold"
              >
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer data-testid="main-footer" className="footer-dark py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        {/* Logo & Description */}
        <div className="md:col-span-2">
          <img src={ASSETS.mainLogo} alt="Gawd Blends" className="h-12 mb-4" />
          <p className="text-white/70 text-sm leading-relaxed max-w-sm">
            Premium herbal blends designed to support your body and mind. One scoop, one routine, real results.
          </p>
        </div>
        
        {/* Quick Links */}
        <div>
          <h4 className="text-[#c5a059] font-bold mb-4 uppercase tracking-wider text-sm">Quick Links</h4>
          <ul className="space-y-2">
            <li><a href="#shop" className="text-white/70 hover:text-white text-sm">Shop</a></li>
            <li><a href="#ingredients" className="text-white/70 hover:text-white text-sm">Ingredients</a></li>
            <li><a href="#reviews" className="text-white/70 hover:text-white text-sm">Reviews</a></li>
            <li><a href="#story" className="text-white/70 hover:text-white text-sm">Our Story</a></li>
            <li><a href="#faq" className="text-white/70 hover:text-white text-sm">FAQ</a></li>
            <li><a href="#contact" className="text-white/70 hover:text-white text-sm">Contact</a></li>
          </ul>
        </div>
        
        {/* Policies */}
        <div>
          <h4 className="text-[#c5a059] font-bold mb-4 uppercase tracking-wider text-sm">Policies</h4>
          <ul className="space-y-2">
            <li><span className="text-white/70 text-sm">Privacy Policy</span></li>
            <li><span className="text-white/70 text-sm">Terms of Service</span></li>
            <li><span className="text-white/70 text-sm">Shipping Policy</span></li>
            <li><span className="text-white/70 text-sm">No Refunds Policy</span></li>
          </ul>
        </div>
      </div>
      
      {/* Social Links */}
      <div className="flex justify-center gap-6 mb-8">
        <a href="https://instagram.com/thegawdblend" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c5a059]">
          <Instagram size={24} />
        </a>
        <a href="https://tiktok.com/@itzomilola" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c5a059]">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
        </a>
        <a href="https://facebook.com/TheGawdBodyBlend" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c5a059]">
          <Facebook size={24} />
        </a>
      </div>
      
      {/* FDA Disclaimer */}
      <div className="border-t border-white/10 pt-8 text-center">
        <p className="fda-disclaimer text-white/50">
          *These statements have not been evaluated by the Food and Drug Administration. 
          This product is not intended to diagnose, treat, cure, or prevent any disease.
        </p>
        <p className="text-white/50 text-sm mt-4">
          © {new Date().getFullYear()} Gawd Blends. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Success Page Component
const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;
        
        if (data.payment_status === "paid") {
          setStatus("success");
          setOrder(data.order);
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (pollCount < 5) {
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("pending");
          setOrder(data.order);
        }
      } catch (error) {
        console.error("Error checking status:", error);
        if (pollCount < 5) {
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollStatus();
  }, [sessionId, navigate, pollCount]);

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-6" data-testid="success-page">
      <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg max-w-lg w-full text-center">
        {status === "loading" && (
          <>
            <div className="spinner mx-auto mb-6"></div>
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Your Order</h1>
            <p className="text-[#6b7280]">Please wait while we confirm your payment...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="success-checkmark mx-auto mb-6">
              <Check size={40} className="text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-2 text-[#1a1a1a]">Thank You!</h1>
            <p className="text-[#6b7280] mb-6">Your order has been confirmed.</p>
            
            {order && (
              <div className="bg-[#fafafa] rounded-xl p-6 mb-6 text-left">
                <h3 className="font-bold mb-4">Order Summary</h3>
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between mb-2">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span>${item.total_price.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm text-[#6b7280] mb-2">
                  <span>Shipping</span>
                  <span>${order.shipping?.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span>${order.total?.toFixed(2)}</span>
                </div>
              </div>
            )}
            
            <p className="text-sm text-[#6b7280] mb-6">
              A confirmation email will be sent to you shortly. 
              Your order will ship within 1-2 business days.
            </p>
            
            <Button 
              onClick={() => navigate("/")}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-3"
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
          </>
        )}
        
        {status === "expired" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <X size={40} className="text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Expired</h1>
            <p className="text-[#6b7280] mb-6">Your payment session has expired. Please try again.</p>
            <Button 
              onClick={() => navigate("/#shop")}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-3"
            >
              Try Again
            </Button>
          </>
        )}
        
        {status === "pending" && (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-600"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Pending</h1>
            <p className="text-[#6b7280] mb-6">
              Your payment is being processed. You'll receive a confirmation email once complete.
            </p>
            <Button 
              onClick={() => navigate("/")}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-3"
            >
              Back to Home
            </Button>
          </>
        )}
        
        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <X size={40} className="text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-[#6b7280] mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Button 
              onClick={() => navigate("/#contact")}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-3"
            >
              Contact Support
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Newsletter Popup Component
const NewsletterPopup = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [discount, setDiscount] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.discount) {
        setDiscount(response.data.discount);
      }
      if (response.data.status === "exists") {
        toast.info("You're already subscribed - here's your code again!");
        setSubmitted(true);
      } else {
        setSubmitted(true);
        toast.success("Welcome to the Gawd Blends family!");
      }
      localStorage.setItem("newsletter_subscribed", "true");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyCode = async () => {
    if (!discount?.code) return;
    try {
      await navigator.clipboard.writeText(discount.code);
      toast.success("Code copied!");
    } catch {
      toast.info(`Code: ${discount.code}`);
    }
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl p-8" data-testid="newsletter-popup">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <DialogTitle className="font-heading text-2xl font-bold text-[#1a1a1a] mb-2">You're In!</DialogTitle>
            <DialogDescription className="text-[#6b7280]">
              {discount
                ? `Use the code below at checkout to save ${discount.percent_off}% on Gawd Body or Gawd Mind.`
                : "Thanks for subscribing. Stay tuned for exclusive offers and wellness tips."}
            </DialogDescription>
            
            {discount && (
              <div className="mt-6 mx-auto max-w-xs" data-testid="newsletter-discount-block">
                <div className="border-2 border-dashed border-[#c5a059] rounded-xl bg-[#fff8e7] px-4 py-5">
                  <p className="text-xs uppercase tracking-widest text-[#c5a059] font-semibold mb-1">
                    Your {discount.percent_off}% off code
                  </p>
                  <p className="font-heading text-3xl font-bold text-[#1a1a1a] tracking-wider" data-testid="newsletter-discount-code">
                    {discount.code}
                  </p>
                  <p className="text-xs text-[#6b7280] mt-2">
                    Enter at checkout. Valid on Gawd Body & Gawd Mind only.
                  </p>
                </div>
                <Button
                  onClick={handleCopyCode}
                  variant="outline"
                  className="mt-3 w-full border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full"
                  data-testid="newsletter-copy-code-btn"
                >
                  Copy Code
                </Button>
              </div>
            )}
            
            <Button 
              onClick={onClose}
              className="mt-6 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8 py-3"
              data-testid="newsletter-continue-btn"
            >
              Continue Shopping
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl p-8" data-testid="newsletter-popup">
        <DialogHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-4">
            <Mail size={28} className="text-[#1E3A8A]" />
          </div>
          <DialogTitle className="font-heading text-2xl font-bold text-[#1a1a1a]">Get 10% Off</DialogTitle>
          <DialogDescription className="text-[#6b7280] mt-2">
            Subscribe for a welcome discount, exclusive offers, and wellness tips.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-12 rounded-lg border-gray-200 focus:border-[#c5a059] focus:ring-[#c5a059]"
            data-testid="newsletter-email-input"
          />
          <Button 
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full py-3 font-semibold"
            data-testid="newsletter-submit-btn"
          >
            {submitting ? "Subscribing..." : "Get My 10% Off Code"}
          </Button>
        </form>
        <p className="text-xs text-center text-[#9ca3af] mt-4">
          No spam, unsubscribe anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Analytics tracker hook
const useAnalytics = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view
    const trackPageView = async () => {
      try {
        await axios.post(`${API}/analytics/pageview`, {
          page: location.pathname,
          referrer: document.referrer
        });
      } catch (error) {
        // Silently fail analytics
      }
    };
    trackPageView();
  }, [location.pathname]);
};

// Home Page Component
const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  
  useAnalytics();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      }
    };
    fetchProducts();
    
    // Show newsletter popup after 5 seconds if not already subscribed
    const hasSubscribed = localStorage.getItem("newsletter_subscribed");
    const hasSeenPopup = sessionStorage.getItem("newsletter_popup_shown");
    
    if (!hasSubscribed && !hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowNewsletter(true);
        sessionStorage.setItem("newsletter_popup_shown", "true");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCheckout = async (productId, quantity) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        product_id: productId,
        quantity: quantity,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to initiate checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <HeroSection />
      <ShopSection products={products} onCheckout={handleCheckout} loading={loading} />
      <IngredientsSection />
      <ReviewsSection />
      <StorySection />
      <FAQSection />
      <ContactSection />
      <Footer />
      <NewsletterPopup isOpen={showNewsletter} onClose={() => setShowNewsletter(false)} />
    </div>
  );
};

// Admin Login Page
const AdminLogin = () => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.get(`${API}/admin/verify/${token}`)
        .then(() => navigate("/admin/dashboard"))
        .catch(() => localStorage.removeItem("admin_token"));
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", response.data.token);
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-6" data-testid="admin-login">
      <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md w-full">
        <div className="text-center mb-8">
          <img src={ASSETS.mainLogo} alt="Gawd Blends" className="h-12 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-[#1a1a1a]">Admin Access</h1>
          <p className="text-[#6b7280] text-sm mt-2">Enter your password to continue</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-12 rounded-lg"
            data-testid="admin-password-input"
          />
          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full py-3"
            data-testid="admin-login-btn"
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <a href="/" className="text-sm text-[#6b7280] hover:text-[#1E3A8A]">← Back to Store</a>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");

  const fetchDashboard = useCallback(async () => {
    if (!token) {
      navigate("/admin");
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/dashboard/${token}`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const fetchAdminProducts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/products/${token}`);
      setProducts(res.data);
      const edits = {};
      res.data.forEach(p => { edits[p.id] = p.price.toFixed(2); });
      setPriceEdits(edits);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchDashboard();
    fetchAdminProducts();
  }, [fetchDashboard, fetchAdminProducts]);

  const handlePriceSave = async (productId) => {
    const newPrice = parseFloat(priceEdits[productId]);
    if (Number.isNaN(newPrice) || newPrice < 0.5) {
      toast.error("Enter a valid price (min $0.50)");
      return;
    }
    setSavingId(productId);
    try {
      await axios.put(`${API}/admin/products/${productId}/${token}`, { price: newPrice });
      toast.success("Price updated");
      await fetchAdminProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update price");
    } finally {
      setSavingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={ASSETS.mainLogo} alt="Gawd Blends" className="h-8" />
            <span className="text-sm font-medium text-[#6b7280]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[#6b7280] hover:text-[#1E3A8A]">View Store</a>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="text-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign size={20} className="text-green-600" />
              <span className="text-sm text-[#6b7280]">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a1a]">${stats.total_revenue?.toFixed(2) || "0.00"}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <ShoppingBag size={20} className="text-[#1E3A8A]" />
              <span className="text-sm text-[#6b7280]">Orders</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a1a]">{stats.paid_orders || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <Users size={20} className="text-purple-600" />
              <span className="text-sm text-[#6b7280]">Subscribers</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a1a]">{stats.total_subscribers || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <Eye size={20} className="text-orange-500" />
              <span className="text-sm text-[#6b7280]">Page Views</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a1a]">{stats.total_pageviews || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare size={20} className="text-teal-600" />
              <span className="text-sm text-[#6b7280]">Messages</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a1a]">{stats.total_contacts || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={20} className="text-[#c5a059]" />
              <span className="text-sm text-[#6b7280]">Conversion</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a1a]">
              {stats.total_orders > 0 ? ((stats.paid_orders / stats.total_orders) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {["overview", "products", "orders", "subscribers", "messages"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab 
                  ? "text-[#1E3A8A] border-b-2 border-[#1E3A8A]" 
                  : "text-[#6b7280] hover:text-[#1a1a1a]"
              }`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Page Views Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-[#1a1a1a] mb-4">Page Views (Last 7 Days)</h3>
              <div className="space-y-3">
                {data?.pageviews_by_page?.map((pv, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-[#6b7280]">{pv._id || "/"}</span>
                    <span className="text-sm font-medium">{pv.count} views</span>
                  </div>
                )) || <p className="text-sm text-[#6b7280]">No data yet</p>}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-[#1a1a1a] mb-4">Recent Orders</h3>
              <div className="space-y-3">
                {data?.recent_orders?.slice(0, 5).map((order, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium">{order.items?.[0]?.product_name}</p>
                      <p className="text-xs text-[#6b7280]">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">${order.total?.toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        order.payment_status === "paid" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {order.payment_status}
                      </span>
                    </div>
                  </div>
                )) || <p className="text-sm text-[#6b7280]">No orders yet</p>}
              </div>
            </div>
          </div>
        )}

        {activeTab === "products" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="admin-products-panel">
            <div className="mb-6">
              <h3 className="font-semibold text-[#1a1a1a] text-lg">Manage Product Prices</h3>
              <p className="text-sm text-[#6b7280] mt-1">Update store prices in real time. Shipping flat rate ($6.00) is configured separately.</p>
            </div>
            <div className="space-y-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors"
                  data-testid={`admin-product-row-${product.id}`}
                >
                  <img src={product.image} alt={product.name} className="w-16 h-16 rounded-lg object-cover bg-gray-50" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1a1a1a]">{product.name}</p>
                    <p className="text-xs text-[#6b7280] line-clamp-2">{product.description}</p>
                    <p className="text-xs text-[#9ca3af] mt-1">Current: ${product.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#6b7280] text-sm">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.50"
                      value={priceEdits[product.id] ?? ""}
                      onChange={(e) => setPriceEdits({ ...priceEdits, [product.id]: e.target.value })}
                      className="w-28"
                      data-testid={`admin-price-input-${product.id}`}
                    />
                    <Button
                      onClick={() => handlePriceSave(product.id)}
                      disabled={savingId === product.id || parseFloat(priceEdits[product.id]) === product.price}
                      className="bg-[#1E3A8A] hover:bg-[#172554] text-white"
                      data-testid={`admin-save-price-${product.id}`}
                    >
                      {savingId === product.id ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              ))}
              {products.length === 0 && (
                <p className="text-center text-[#6b7280] py-8">Loading products...</p>
              )}
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Qty</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.recent_orders?.map((order, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono">{order.id?.slice(0, 8)}...</td>
                      <td className="px-6 py-4 text-sm">{order.items?.[0]?.product_name}</td>
                      <td className="px-6 py-4 text-sm">{order.items?.[0]?.quantity}</td>
                      <td className="px-6 py-4 text-sm font-medium">${order.total?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          order.payment_status === "paid" 
                            ? "bg-green-100 text-green-700" 
                            : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6b7280]">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!data?.recent_orders || data.recent_orders.length === 0) && (
                <div className="text-center py-12 text-[#6b7280]">No orders yet</div>
              )}
            </div>
          </div>
        )}

        {activeTab === "subscribers" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Subscribed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#6b7280] uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.recent_subscribers?.map((sub, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">{sub.email}</td>
                      <td className="px-6 py-4 text-sm text-[#6b7280]">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                          Active
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!data?.recent_subscribers || data.recent_subscribers.length === 0) && (
                <div className="text-center py-12 text-[#6b7280]">No subscribers yet</div>
              )}
            </div>
          </div>
        )}

        {activeTab === "messages" && (
          <div className="space-y-4">
            {data?.recent_contacts?.map((contact, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-[#1a1a1a]">{contact.name}</h4>
                    <p className="text-sm text-[#6b7280]">{contact.email}</p>
                  </div>
                  <span className="text-xs text-[#9ca3af]">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-[#374151] bg-gray-50 p-4 rounded-lg">{contact.message}</p>
              </div>
            ))}
            {(!data?.recent_contacts || data.recent_contacts.length === 0) && (
              <div className="text-center py-12 text-[#6b7280] bg-white rounded-xl">No messages yet</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App font-body">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
